import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import {
  Button,
  ToggleSwitch,
  Breadcrumb,
  TextInput,
  Label,
} from "flowbite-react";
import { HiHome, HiPlus, HiFilter } from "react-icons/hi";
import ExportMenu from "@/components/common/ExportMenu";
import { fetchAllPages } from "@/utils/listExport";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateClientGroupMutation,
  useGetClientGroupsQuery,
  useLazyGetClientGroupsQuery,
} from "@/api/clientGroupApi/clientGroup";
import ClientGroupTable from "@/components/clientGroups/ClientGroupTable";
import ClientGroupFormDialog from "@/components/clientGroups/ClientGroupFormDialog";
import { ClientGroup, DataGridPagination } from "@/sharedTypes";
import LoadingPage from "@/components/LoadingPage";
import { toast } from "@/lib/toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

const ClientGroupList = () => {
  const { t } = useTranslation(["clientGroup", "common"]);
  const user = useCurrentUser();
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showInactive, setShowInactive] = useState(false);
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  const [groupNameFilter, setGroupNameFilter] = useState("");
  const [debouncedGroupName, setDebouncedGroupName] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedGroupName(groupNameFilter);
    }, 500);
    return () => clearTimeout(handler);
  }, [groupNameFilter]);

  useEffect(() => {
    setPage(1);
  }, [debouncedGroupName, showInactive]);

  const handleClearFilters = () => {
    setGroupNameFilter("");
    setShowInactive(false);
  };

  const handleToggleFilters = () => {
    if (showFilters) {
      handleClearFilters();
    }
    setShowFilters(!showFilters);
  };

  const queryParams = {
    pagination: { page, pageSize },
    filters: {
      is_active: !showInactive,
      ...(debouncedGroupName ? { group_name: debouncedGroupName } : {}),
    },
  };

  const { data: clientGroupsData, isLoading } =
    useGetClientGroupsQuery(queryParams);

  const [fetchAllClientGroups] = useLazyGetClientGroupsQuery();

  // Whole filtered set, not just the visible page.
  const fetchExportRows = () =>
    fetchAllPages((page, pageSize) =>
      fetchAllClientGroups({ ...queryParams, pagination: { page, pageSize } }).unwrap()
    );

  const exportFilters = [
    { label: t("common:status"), value: showInactive ? t("common:inactive") : t("common:active") },
    ...(debouncedGroupName
      ? [{ label: t("clientGroup:groupName"), value: debouncedGroupName }]
      : []),
  ];

  const [createClientGroup] = useCreateClientGroupMutation();

  const handleCreateSubmit = async (data: ClientGroup) => {
    try {
      await createClientGroup(data).unwrap();
      toast.success(t("clientGroup:groupCreated"));
      setIsCreationModalOpen(false);
    } catch (error) {
      toast.error(t("clientGroup:createFailed"));
    }
  };

  const paginationData = clientGroupsData?.pagination;
  const totalItems =
    paginationData?.totalItems ?? clientGroupsData?.items?.length ?? 0;
  const calculatedTotalPages =
    Math.ceil(totalItems / (paginationData?.pageSize || pageSize)) || 1;

  const pagination: DataGridPagination = {
    pageIndex: paginationData?.pageIndex || 1,
    pageSize: paginationData?.pageSize || 10,
    totalPages: paginationData?.totalPages ?? calculatedTotalPages,
    totalItems: totalItems,
  };

  return (
    <NavbarSidebarLayout>
      {isLoading ? (
        <LoadingPage />
      ) : (
        <div className="h-screen overflow-y-auto pb-20">
          <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
            <div className="col-span-full">
              <Breadcrumb className="mb-4">
                <Breadcrumb.Item href="/">
                  <div className="flex items-center gap-x-3">
                    <HiHome className="text-xl" />
                    <span className="dark:text-white">
                      {t("common:home")}
                    </span>
                  </div>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  {t("clientGroup:title")}
                </Breadcrumb.Item>
              </Breadcrumb>

              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                    {t("clientGroup:title")}
                  </h1>
                  <p className="text-gray-500">
                    {t("clientGroup:manageClientGroups")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <ExportMenu
                    title={t("clientGroup:title")}
                    filename="musteri-gruplari"
                    filters={exportFilters}
                    fetchRows={fetchExportRows}
                    columns={[
                      { header: "ID", value: (r: any) => r.client_group_id ?? r.id ?? "" },
                      { header: t("clientGroup:groupName"), value: (r: any) => r.group_name ?? "" },
                      { header: t("category:description"), value: (r: any) => r.group_description ?? "" },
                      { header: t("common:status"), value: (r: any) => (r.is_active ? t("common:active") : t("common:inactive")) },
                    ]}
                  />
                  {isCompanyAdminUser(user) && (
                    <Button
                      color="primary"
                      onClick={() => setIsCreationModalOpen(true)}
                    >
                      <HiPlus className="mr-2 h-5 w-5" />
                      {t("clientGroup:createClientGroup")}
                    </Button>
                  )}
                </div>
              </div>
              <div className="mt-6 flex justify-start">
                <Button color="light" onClick={handleToggleFilters}>
                  <HiFilter className="mr-2 h-5 w-5" />
                  {showFilters
                    ? t("common:hideFilters")
                    : t("common:showFilters")}
                </Button>
              </div>

              {showFilters && (
                <div className="mt-6 rounded-lg bg-white p-4 shadow dark:bg-gray-800">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                    {t("common:filters")}
                  </h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div>
                      <Label htmlFor="group-search" className="mb-2 block">
                        {t("clientGroup:groupName")}
                      </Label>
                      <TextInput
                        id="group-search"
                        placeholder={t("clientGroup:searchGroup")}
                        className="mt-4"
                        value={groupNameFilter}
                        onChange={(e) => setGroupNameFilter(e.target.value)}
                      />
                    </div>

                    <div className="flex flex-col justify-end gap-4 pb-2">
                      <ToggleSwitch
                        checked={showInactive}
                        label={t("clientGroup:showInactive")}
                        onChange={setShowInactive}
                      />
                      <Button
                        color="light"
                        size="sm"
                        onClick={handleClearFilters}
                      >
                        {t("common:reset")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <ClientGroupTable
              data={clientGroupsData?.items || []}
              pagination={pagination}
              onPageChange={setPage}
              showInactive={showInactive}
            />
          </div>

          <ClientGroupFormDialog
            open={isCreationModalOpen}
            onClose={() => setIsCreationModalOpen(false)}
            onSubmit={handleCreateSubmit}
            mode="create"
          />
        </div>
      )}
    </NavbarSidebarLayout>
  );
};

export default ClientGroupList;
